import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FILTER_FIELD_TYPE, ROLE_TYPES } from '../../utils/DataConstants';
import { Button, Modal, Popconfirm, Tag } from 'antd';
import { EmptyState } from '../empty_state/EmptyState';
import { useHistory, useLocation, withRouter } from 'react-router';
import { connect } from 'react-redux';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import { CONTRACTOR_CLIENTS_CRUD_ACTION_CREATORS } from '../../actions/contractor_clients_actions';
import {
	contractorClientsRestCrudThunksForBuyer,
	contractorClientsRestCrudThunksForSupplier,
} from '../../thunks/contractor_clients_thunks';
import { getStateSliceProp, nullSafeGet, stopDefaultEvents } from '../../utils/DataAccessUtils';
import OWRadioGroup from '../ow_radio_group/OWRadioGroup';
import { Link } from 'react-router-dom';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import { clearNavbarState } from '../../actions/navbar_actions';

const SC_TC_NAME = 'supplierCustomersIndex';

export const SelfManagedTag = <Tag color="#2792b0">Self Managed</Tag>;
export const ConnectedTag = <Tag color="green">Connected</Tag>;

const ContractorClientRowDisplay = ({ client, userType, location }) => {
	return (
		<Link
			className={'ActionLink flex-1'}
			to={getLinkWIthBackLinkParams(
				location,
				'Back to clients',
				`/${userType}/contractor_clients/detail/${client.id}/details`
			)}
		>
			<div className="flex flex-col">
				<div className="text-xl text-gray-500 ">{client.name}</div>
				<div className="mt-2">
					{nullSafeGet('clientBuyerCompany', client) ||
					nullSafeGet('clientSupplierFacility', client)
						? ConnectedTag
						: SelfManagedTag}
				</div>
			</div>
		</Link>
	);
};

const ContractorClientsIndexPage: FC<any> = ({
	updateFilters,
	supplierCustomers,
	clearAndUpdateFilters,
	fetchCustomers,
	userType,
	removeClient,
	clearNavbar,
}): React.ReactElement => {
	const [deletingClientId, setDeletingClientId] = useState(null);
	const history = useHistory();
	const location = useLocation();

	useEffect(() => {
		clearNavbar();
	}, []);

	const refreshCustomers = useCallback(() => {
		fetchCustomers(
			{},
			SC_TC_NAME,
			getStateSliceProp('pagination', SC_TC_NAME, supplierCustomers),
			getStateSliceProp('sorting', SC_TC_NAME, supplierCustomers),
			getStateSliceProp('filters', SC_TC_NAME, supplierCustomers)
		);
	}, [fetchCustomers, supplierCustomers]);

	const onDelete = useCallback(
		(client) => {
			setDeletingClientId(client.id);
			removeClient(client)
				.then(() => {
					refreshCustomers();
				})
				.finally(() => setDeletingClientId(null));
		},
		[refreshCustomers, removeClient]
	);

	const columns = useMemo(
		() => [
			{
				render: (_, record) => (
					<ContractorClientRowDisplay client={record} userType={userType} location={location} />
				),
			},
			{
				render: (_, record) => (
					<div className="flex flex-grow-0 flex-row justify-end">
						<Link
							className={'ActionLink'}
							to={getLinkWIthBackLinkParams(
								location,
								'Back to clients',
								`/${userType}/contractor_clients/edit/${record.id}`
							)}
						>
							<Button type={'ghost'} key="edit">
								<i className="icons8-font icons8-font-margin-right icons8-edit"></i> Edit Client
							</Button>
						</Link>
						<div className="ml-2">
							<Popconfirm
								title={'Are you sure, you want remove this client?'}
								onConfirm={(e) => {
									stopDefaultEvents(e);
									onDelete(record);
								}}
								onCancel={(e) => stopDefaultEvents(e)}
								okText="Yes"
							>
								<Button
									loading={deletingClientId === record.id}
									danger
									ghost
									onClick={stopDefaultEvents}
								>
									Remove Client
								</Button>
							</Popconfirm>
						</div>
					</div>
				),
			},
		],
		[deletingClientId, location, onDelete, userType]
	);

	return (
		<>
			<PaginatedReduxTableWithHeader
				targetCollectionName={SC_TC_NAME}
				stateSlice={supplierCustomers}
				updateFilters={updateFilters}
				clearAndUpdateFilters={clearAndUpdateFilters}
				filterConfig={[
					{
						fieldName: 'isSelfManaged',
						type: FILTER_FIELD_TYPE.RADIO_GROUP,
						items: [
							{
								label: 'All',
								value: 'undefined',
							},
							{
								label: 'Self Managed',
								value: 'true',
							},
							{
								label: 'Connected',
								value: 'false',
							},
						],
						filtersValueAccessor: (filtersStateSlice) =>
							nullSafeGet('isSelfManaged', filtersStateSlice),
						defaultValue: undefined,
						keyAccessor: (item) => item.value,
						valueAccessor: (item) => item.value,
						handleChange: (isSelfManaged) => {
							updateFilters(
								{ isSelfManaged: isSelfManaged === 'undefined' ? undefined : isSelfManaged },
								SC_TC_NAME
							);
						},
						renderRecord: (item) => item.label,
						component: OWRadioGroup,
						showOutsideDropdown: true,
					},
				]}
				entityCollectionName="contractor_clients"
				tableColumns={columns}
				initialPagination={{ current: 1, pageSize: 10 }}
				fetchData={
					userType === ROLE_TYPES.SUPPLIER
						? contractorClientsRestCrudThunksForSupplier.read
						: contractorClientsRestCrudThunksForBuyer.read
				}
				handleQueryParams
				rightActions={
					<Button
						size="large"
						type="primary"
						onClick={() => {
							history.push(`/${userType}/contractor_clients/overview/new`);
						}}
					>
						Add Client
					</Button>
				}
				emptyState={
					<EmptyState
						graphic={
							<img
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/letter owl.svg"
								alt={`There are no clients for you!`}
							/>
						}
						headline={`There are no clients for you!`}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 560, marginBottom: 16 }}>
									Please add a new location to get started.
								</div>
							</div>
						}
					/>
				}
			/>
		</>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	supplierCustomers: state.contractor_clients,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateFilters: (filters, targetCollection) =>
		dispatch(CONTRACTOR_CLIENTS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(
			CONTRACTOR_CLIENTS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)
		),
	fetchCustomers: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contractorClientsRestCrudThunksForSupplier
				: contractorClientsRestCrudThunksForBuyer;
		return dispatch(
			fnToCall.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		);
	},
	removeClient: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contractorClientsRestCrudThunksForSupplier.delete(entity, 'id', null, false, true)
				: contractorClientsRestCrudThunksForBuyer.delete(entity, 'id', null, false, true)
		),
	clearNavbar: () => dispatch(clearNavbarState()),
});

const ComponentWithoutUserType = withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(ContractorClientsIndexPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
